import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Navbar from "../../components/Navbar/Navbar";
import CustomHeader from "../../components/CustomHeader/CustomHeader";
import "./PreventiveActions.css";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PreventiveActions = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchActions = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await axios.get("/gchat/preventive-actions", { params });
      setActions(res.data);
    } catch (err) {
      console.error("Failed to fetch preventive actions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const handleSearch = () => {
    const params = {};
    if (searchText) params.search = searchText;
    fetchActions(params);
  };

  const handleClear = () => {
    setSearchText("");
    fetchActions();
  };

  const handleDownloadCsv = async () => {
    try {
      const params = {};
      if (searchText) params.search = searchText;
      const res = await axios.get("/gchat/preventive-actions/csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "preventive_actions.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download CSV.");
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!window.confirm("Delete this preventive action?")) return;
    try {
      await axios.delete(`/gchat/preventive-actions/${actionId}`);
      setActions((prev) => prev.filter((a) => a.id !== actionId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete action.");
    }
  };

  const onSelectionChanged = useCallback((event) => {
    const selected = event.api.getSelectedRows().map((r) => r.id);
    setSelectedIds(selected);
  }, []);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(`Delete ${selectedIds.length} selected action(s)?`)
    )
      return;
    try {
      await axios.post("/gchat/preventive-actions/bulk-delete", {
        ids: selectedIds,
      });
      setActions((prev) => prev.filter((a) => !selectedIds.includes(a.id)));
      setSelectedIds([]);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete actions.");
    }
  };

  const columnDefs = [
    {
      headerName: "Preventive Action",
      field: "action_text",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 2,
      minWidth: 300,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return <div className="pa-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "Source Issue",
      field: "source_issue",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return <div className="pa-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "RCA Document",
      field: "doc_link",
      sortable: false,
      filter: false,
      resizable: true,
      width: 140,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="pa-link"
          >
            Open Doc
          </a>
        );
      },
    },
    {
      headerName: "Space",
      field: "space_display_name",
      sortable: true,
      filter: true,
      resizable: true,
      width: 160,
      headerComponentFramework: CustomHeader,
      valueFormatter: (params) => params.value || "—",
    },
    {
      headerName: "Date",
      field: "conversation_start",
      sortable: true,
      filter: true,
      resizable: true,
      width: 190,
      headerComponentFramework: CustomHeader,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "",
      field: "id",
      sortable: false,
      filter: false,
      resizable: false,
      width: 80,
      cellRenderer: (params) => (
        <button
          className="pa-delete-btn"
          onClick={() => handleDeleteAction(params.value)}
          title="Delete this action"
        >
          Delete
        </button>
      ),
    },
  ];

  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  return (
    <div>
      <Navbar />
      <div className="pa-container">
        <div className="pa-header">
          <h1>Preventive Actions</h1>
          <p>
            Preventive and corrective actions extracted from RCA documents
            linked in Google Chat conversations.
          </p>
        </div>

        <div className="pa-card">
          <div className="pa-filter-bar">
            <input
              type="text"
              className="pa-filter-input"
              placeholder="Search actions or issues..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button className="pa-filter-btn" onClick={handleSearch}>
              Search
            </button>
            <button className="pa-filter-clear-btn" onClick={handleClear}>
              Clear
            </button>
            <button className="pa-csv-btn" onClick={handleDownloadCsv} title="Download as CSV">
              Download CSV
            </button>
          </div>

          {loading ? (
            <p className="pa-no-data">Loading...</p>
          ) : actions.length === 0 ? (
            <p className="pa-no-data">
              No preventive actions yet. Run a chat analysis job on
              conversations that contain RCA document links.
            </p>
          ) : (
            <>
              {selectedIds.length > 0 && (
                <div className="pa-bulk-actions">
                  <span className="pa-bulk-count">
                    {selectedIds.length} selected
                  </span>
                  <button className="pa-delete-btn" onClick={handleBulkDelete}>
                    Delete Selected
                  </button>
                </div>
              )}
              <div
                className="ag-theme-quartz pa-grid-wrapper"
                style={{
                  "--ag-header-background-color": "rgb(61, 39, 255)",
                  "--ag-header-foreground-color": "#fff",
                }}
              >
                <AgGridReact
                  rowData={actions}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  onSelectionChanged={onSelectionChanged}
                  pagination={true}
                  paginationPageSize={20}
                  domLayout="autoHeight"
                  animateRows={true}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreventiveActions;
